<template>
  <div>
    <v-row>
      <v-col cols="12" class="py-0">
        <v-combobox
          v-model="selectedItem"
          :hint="$t('forms.hint.item')"
          :items="items"
          :search-input.sync="search"
          item-value="person.id"
          item-text="person.name_text"
          return-object
          persistent-hint
          v-if="!disabledForm"
          @change="changeCombobox"
          @input="addPersonCharge(selectedItem)"
        >
          <template v-slot:no-data>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>
                  {{ $t("forms.comboboxPeople.nodata") }} "<strong>{{
                    search
                  }}</strong
                  >". {{ $t("forms.comboboxPeople.nodata_1") }} <kbd>enter</kbd>
                  {{ $t("forms.comboboxPeople.nodata_2") }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>
        </v-combobox>
      </v-col>
    </v-row>
    <v-row v-if="!disabledForm && hasAutoFillSource">
      <v-col cols="12" class="py-0">
        <v-btn
          small
          color="primary"
          class="mt-2"
          @click="autoFillFromAuthors"
          :disabled="autoFillDisabled"
        >
          <v-icon left small>mdi-auto-fix</v-icon>
          {{ $t('forms.comboboxPeople.magicFill') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-simple-table :class="changeColor ? 'border-box amber' : 'border-box'" v-if="itemsSelected !== undefined && itemsSelected.length">
          <template v-slot:default>
            <thead>
              <tr>
                <th class="text-center">{{ $t("incharge.postion") }}</th>
                <th class="text-center">{{ $t("form.name") }}</th>
                <th class="text-center"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in itemsSelected" :key="index">
                <td width="50px">
                  <v-text-field
                    :disabled="disabledForm"
                    v-model="item.position"
                    type="number"
                    @change="$emit('change')"
                  />
                </td>
                <td>
                  {{
                    item.person.preferred_name.given_name +
                      " " +
                      item.person.preferred_name.family_name
                  }}
                </td>
                <td>
                  <v-icon
                    :disabled="disabledForm"
                    color="red"
                    @click="remove(item)"
                  >
                    mdi-delete
                  </v-icon>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import axios from "axios";

export default {
  props: {
    itemsSelected: Array,
    disabledForm: Boolean,
    changeColor: Boolean,
    textAuthors: {
      type: String,
      default: null
    }
  },
  name: "selectPeopleComboBox",

  data: () => ({
    temporalIdCounter: -1,
    items: [],
    selectedItem: "",
    search: ""
  }),
  created() {
    this.initialize();
  },

  methods: {
    autoFillFromAuthors() {
      if (this.disabledForm || !this.hasAutoFillSource) {
        return;
      }
      const authorsText = (this.textAuthors || "").trim();
      if (!authorsText) {
        this.notify("warning", this.$t("forms.comboboxPeople.magicFill_noText"));
        return;
      }
      const authors = this.parseAuthors(authorsText);
      if (!authors.length) {
        this.notify("warning", this.$t("forms.comboboxPeople.magicFill_noText"));
        return;
      }
      if (!this.items.length) {
        this.notify("warning", this.$t("forms.comboboxPeople.magicFill_noData"));
        return;
      }
      const matches = [];
      const usedIds = new Set();
      authors.forEach((author, index) => {
        const matched = this.findBestMatch(author, usedIds);
        if (matched) {
          usedIds.add(matched.person.id);
          matches.push({
            person: this.clonePerson(matched.person),
            position: String(index + 1)
          });
        }
      });
      if (!matches.length) {
        this.notify("info", this.$t("forms.comboboxPeople.magicFill_noMatches"));
        return;
      }
      this.itemsSelected.splice(0, this.itemsSelected.length, ...matches);
      const unmatched = authors.length - matches.length;
      if (unmatched > 0) {
        this.notify(
          "info",
          this.$t("forms.comboboxPeople.magicFill_partial", {
            count: matches.length,
            remaining: unmatched
          })
        );
      } else {
        this.notify(
          "success",
          this.$t("forms.comboboxPeople.magicFill_success", {
            count: matches.length
          })
        );
      }
      this.$emit("change");
    },
    notify(color, text) {
      if (this.$store && typeof this.$store.commit === "function") {
        this.$store.commit("alert", { color, text });
      }
    },
    clonePerson(person) {
      if (!person) {
        return person;
      }
      return {
        id: person.id,
        temporalId: person.temporalId,
        preferred_name: {
          given_name: person.preferred_name
            ? person.preferred_name.given_name
            : "",
          family_name: person.preferred_name
            ? person.preferred_name.family_name
            : ""
        },
        name_text: person.name_text
      };
    },
    parseAuthors(text) {
      return text
        .replace(/\r?\n/g, ",")
        .replace(/\s+(y|e|and|&)\s+/gi, ",")
        .split(/[,;]+/)
        .map(author => author.trim())
        .filter(Boolean);
    },
    findBestMatch(author, usedIds) {
      let bestMatch = null;
      let bestScore = 0;
      this.items.forEach(item => {
        const person = item.person;
        if (!person || usedIds.has(person.id)) {
          return;
        }
        const score = this.scoreAuthor(author, person);
        if (score > bestScore) {
          bestScore = score;
          bestMatch = item;
        }
      });
      return bestScore >= 4 ? bestMatch : null;
    },
    scoreAuthor(author, person) {
      const parsed = this.tokenizeAuthor(author);
      if (!parsed.surnames.length) {
        return 0;
      }
      const personGivenTokens = this.tokenizeName(
        person.preferred_name ? person.preferred_name.given_name : ""
      );
      const personSurnameTokens = this.tokenizeName(
        person.preferred_name ? person.preferred_name.family_name : ""
      );
      if (!personSurnameTokens.length) {
        return 0;
      }
      const surnameMatches = parsed.surnames.filter(token =>
        personSurnameTokens.includes(token)
      ).length;
      if (!surnameMatches) {
        return 0;
      }
      let score = surnameMatches * 4;
      if (parsed.surnames[0] && parsed.surnames[0] === personSurnameTokens[0]) {
        score += 2;
      }
      if (
        parsed.surnames.length > 0 &&
        parsed.surnames.join(" ") === personSurnameTokens.join(" ")
      ) {
        score += 3;
      }
      const givenScore = this.scoreGivenNameCompatibility(
        parsed.givens,
        personGivenTokens
      );
      if (parsed.givens.length && !givenScore) {
        return 0;
      }
      score += givenScore;
      return score;
    },
    scoreGivenNameCompatibility(authorGivenTokens, personGivenTokens) {
      if (!authorGivenTokens.length || !personGivenTokens.length) {
        return 0;
      }
      return authorGivenTokens.reduce((score, authorGiven) => {
        const tokenScore = personGivenTokens.reduce((bestScore, personGiven) => {
          if (authorGiven.length === 1 && personGiven[0] === authorGiven) {
            return Math.max(bestScore, 2);
          }
          if (personGiven.length === 1 && authorGiven[0] === personGiven) {
            return Math.max(bestScore, 2);
          }
          if (authorGiven === personGiven) {
            return Math.max(bestScore, 3);
          }
          return bestScore;
        }, 0);
        return score + tokenScore;
      }, 0);
    },
    tokenizeAuthor(author) {
      const tokens = this.tokenizeName(author);
      if (!tokens.length) {
        return { givens: [], surnames: [] };
      }
      if (tokens.length === 1) {
        return { givens: [], surnames: tokens };
      }
      let givenEndIndex = 1;
      while (
        givenEndIndex < tokens.length &&
        tokens[givenEndIndex].length === 1
      ) {
        givenEndIndex += 1;
      }
      const givens = tokens.slice(0, Math.min(givenEndIndex, tokens.length - 1));
      const surnames = tokens.slice(givenEndIndex);
      if (!surnames.length) {
        surnames.push(tokens[tokens.length - 1]);
      }
      return { givens, surnames };
    },
    tokenizeName(value) {
      if (!value) {
        return [];
      }
      return this.normalizeName(value)
        .replace(/[^a-z\s-]/g, " ")
        .replace(/-/g, " ")
        .split(/\s+/)
        .filter(Boolean);
    },
    normalizeName(value) {
      return value
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
    },
    getNewTemporalId() {
      let temporalIdCounter = -1;
      this.itemsSelected.forEach(item => {
        if (
          item.person.temporalId !== undefined &&
          item.person.temporalId <= temporalIdCounter
        ) {
          temporalIdCounter = item.person.temporalId - 1;
        }
      });
      return temporalIdCounter;
    },
    initialize() {
      const path = this.$store.state.endpoint + "/personName";
      axios
        .get(path)
        .then(response => {
          response.data.forEach(item => {
            this.items.push({
              person: {
                id: item.id,
                preferred_name: item.preferred_name,
                name_text:
                  item.preferred_name.given_name +
                  " " +
                  item.preferred_name.family_name
              }
            });
          });
        })
        .catch(error => {
          console.log(error);
        });
    },
    addPersonCharge(item) {
      let kk = true;
      if (typeof item === "string") {
        this.items.forEach(relation => {
          const name_text =
            relation.person.preferred_name.given_name +
            " " +
            relation.person.preferred_name.family_name;
          if (item === name_text) {
            item = {
              person: {
                id: relation.person.id,
                preferred_name: relation.person.preferred_name,
                name_text: name_text
              }
            };
            kk = false;
          }
        });
        if (kk) {
          item = {
            person: {
              id: null,
              temporalId: this.getNewTemporalId(),
              preferred_name: {
                given_name: item,
                family_name: ""
              },
              name_text: item
            }
          };
        }
      }
      kk = true;
      this.itemsSelected.forEach(relation => {
        if (
          (item.person.id !== null && relation.person.id === item.person.id) ||
          (item.person.id == null &&
            relation.person.preferred_name.given_name ===
              item.person.preferred_name.given_name)
        ) {
          kk = false;
        }
      });
      if (kk && item.person.preferred_name.given_name !== "") {
        item.position = this.itemsSelected.length
          ? (
              Math.max(
                ...this.itemsSelected.map(function(o) {
                  return o.position;
                })
              ) + 1
            ).toString()
          : "1";
        this.itemsSelected.push(item);
        this.temporalIdCounter--;
      }
      this.$nextTick(() => {
        this.selectedItem = null;
        this.search = "";
      });
    },
    remove(obj) {
      const list = [];
      this.itemsSelected.forEach(item => {
        if (item.person.id != null && item.person.id !== obj.person.id) {
          list.push(item);
        } else if (
          item.person.id == null &&
          item.person.temporalId !== obj.person.temporalId
        ) {
          list.push(item);
        }
      });

      this.itemsSelected.length = 0;
      list.forEach(item => {
        this.itemsSelected.push(item);
      });
      if (this.itemsSelected.length === 0) {
        this.itemsSelected.pop();
      }
      this.$emit("change");
    },

    changeCombobox() {
      this.search = "";
      this.$emit("change");
    }
  },
  computed: {
    autoFillDisabled() {
      if (this.disabledForm || !this.hasAutoFillSource) {
        return true;
      }
      const text = (this.textAuthors || "").trim();
      return !text;
    },
    hasAutoFillSource() {
      const propsData = this.$options && this.$options.propsData;
      if (propsData && Object.prototype.hasOwnProperty.call(propsData, "textAuthors")) {
        return true;
      }
      return this.textAuthors !== null && this.textAuthors !== undefined;
    }
  }
};
</script>

<style scoped></style>
